import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import {
    GetPropCharValuesRequest,
    TopLevelCompany,
    SelectedEntity,
    GenericEntity,
    ReportDetail,
    EntitySelection,
    GetEntitiesRequest,
} from './report.manager.model';
import { ReportManagerSelectEntitiesService } from './select.entities.service';
import { InstanceRights, RestrictService } from '../../Common/Permissions/restrict.service';
import { ReportManagerService } from './report.manager.service';
import { ReportType } from './report-type';
import { ToastrService } from 'ngx-toastr';
import { EntityTypeIds } from '../../constants.new';
import { WeissmanDateFormatPipe } from '../../UI-Lib/Pipes';
import { FormsModule } from '@angular/forms';
import { PanelChevronComponent } from '../../UI-Lib/Weissman-Panel/panel.chevron.component';
import { ScrollToFirstSelectedDirective } from '../../UI-Lib/Directives/Scroll-To-Selected/scrollToFirstSelected.directive';
import { AceLoaderComponent } from '../../UI-Lib/ACE/Loader/loader.component';

declare const _: any;

@Component({
    selector: 'report-manager-select-entities',
    templateUrl: './select.entities.component.html',
    imports: [FormsModule, PanelChevronComponent, ScrollToFirstSelectedDirective, AceLoaderComponent],
})
export class ReportManagerSelectEntitiesComponent implements OnInit {
    constructor(
        private selectEntitiesService: ReportManagerSelectEntitiesService,
        private restrictService: RestrictService,
        private readonly _toastr: ToastrService,
        private reportManagerService: ReportManagerService,
        private readonly _datePipe: WeissmanDateFormatPipe,
    ) {
        this.reportFavoriteChange = new EventEmitter<boolean>();
        this.excludeInactiveChanged = new EventEmitter<boolean>();
        this.entityRowsChanged = new EventEmitter<void>();
    }

    @Input() entitySelection: EntitySelection;
    @Input() dataLoaded: boolean = true;
    @Input() report: ReportDetail;
    @Input() hideTopLevelCompanyPicker: boolean = false;
    @Input() showSupportInstancePicker: boolean = false;
    @Input() collapsible: boolean = false;
    @Input() showApplyButton: boolean;
    @Input() showBody: boolean = true;
    @Output() reportFavoriteChange: EventEmitter<boolean>;
    @Output() entitySelectionChanged: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() excludeInactiveChanged: EventEmitter<boolean>;
    @Output() entityRowsChanged: EventEmitter<void>;
    @Output() showBodyChange: EventEmitter<boolean> = new EventEmitter<boolean>();

    additionalSelections: SelectedEntity[];
    loadingTopLevelCompanies: boolean;
    hasInstancePrivatePermission: boolean;
    reportTypes = ReportType;
    topLevelCompanies: TopLevelCompany[];
    loadingSupportInstances: boolean;
    supportInstances: Core.SupportInstanceInfoModel[];
    headerText: string;
    hasInactiveEntities: boolean;
    showMoreSelections: boolean;
    EntityTypeIds = EntityTypeIds;

    private static readonly _noTopLevelCompaniesSelected: readonly number[] = [];

    // Cached lookup so the top-level company <option> list (which routinely holds well over 10,000
    // entries) can resolve its selected state in O(1) during change detection.
    private _selectedTopLevelCompanyIds: Set<number> = new Set();
    private _selectedTopLevelCompanySource: readonly number[] | null = null;
    private _selectedTopLevelCompanyCount: number = -1;

    async ngOnInit(): Promise<void> {
        this.headerText = (() => {
            switch (this.report.reportTypeId) {
                case ReportType.SupportInstanceEscalationActivity:
                    return 'Select Your Companies/Users';
                case ReportType.AttachmentBundlerJurisdiction:
                    return 'Select your States/Assessors/Collectors';
                default:
                    return 'Select Your Companies/Sites/Parcels';
            }
        })();

        this.hasInactiveEntities = this.report.reportTypeId !== ReportType.SupportInstanceEscalationActivity;

        this.topLevelCompanies = [];
        this.supportInstances = [];
        this.hasInstancePrivatePermission =
            this.restrictService.hasInstanceRight(InstanceRights.PRIVATEITEMSVIEW) ||
            this.restrictService.hasInstanceRight(InstanceRights.PRIVATEITEMSEDIT);

        if (!this.hasInstancePrivatePermission) {
            this.report.criteria.frequentlyUsed = false;
        }

        const initedEntities: SelectedEntity[] = this.selectEntitiesService.getSelectedEntities();
        this.entitySelection.selectedEntities = _.map(
            this.entitySelection.selectedEntities,
            (selectedEntity: SelectedEntity) => {
                const initedEntity = _.find(initedEntities, (x: SelectedEntity) => {
                    if (selectedEntity.isAppealResponsibility && x.isAppealResponsibility) {
                        // Appeal Responsibiliy and Users (not contacts) have the same ID
                        return true;
                    }

                    return (
                        x.id === selectedEntity.id &&
                        !selectedEntity.isAppealResponsibility &&
                        !x.isAppealResponsibility
                    );
                });

                return _.defaults(selectedEntity, initedEntity);
            },
        );

        this.configureAdditionalSelections();

        if (!this.hideTopLevelCompanyPicker) {
            await this.getTopLevelCompanies();
        }

        if (this.showSupportInstancePicker) {
            await this.loadInstances();
            await this.maybeGetSubentities(-1);
        }

        if (this.entitySelection.beginWithStateSource) {
            await this.initStateSource();
        }
    }

    get isCsrCommandCenter(): boolean {
        return this.report.criteria.reportType === ReportType.CsrCommandCenter;
    }

    async getTopLevelCompanies(): Promise<any> {
        this.loadingTopLevelCompanies = true;

        try {
            this.reportFavoriteChange.emit(this.report.criteria.frequentlyUsed);

            const topLevelCompanies = await this.selectEntitiesService.getTopLevelCompanies(
                this.report.criteria.frequentlyUsed,
                this.entitySelection.excludeInactive,
            );
            this.topLevelCompanies = _.sortBy(topLevelCompanies, (item) => item.companyName.toLowerCase());
        } finally {
            this.loadingTopLevelCompanies = false;
        }

        const topLevelCompanyIDs: number[] = _.map(this.topLevelCompanies, 'companyID') as number[];
        if (!this.report.criteria.frequentlyUsed) {
            this.entitySelection.topLevelCompaniesCount = this.topLevelCompanies.length;
        }
        this.entitySelection.selectedTopLevelCompanyIDs = _.intersection(
            this.entitySelection.selectedTopLevelCompanyIDs,
            topLevelCompanyIDs,
        ) as number[];
        this.reportManagerService.topLevelCompanies$.next({
            topLevelCompanyIds: this.entitySelection.selectedTopLevelCompanyIDs,
            initFromSavedReport: this.report.isOwnedByUser,
        });

        if (this.topLevelCompanies.length === 0 || this.entitySelection.selectedTopLevelCompanyIDs.length === 0) {
            this.removeEmptyEntities(0);
            return Promise.resolve();
        }

        return this.maybeGetSubentities(-1);
    }

    async selectionButtonClicked(entityTypeId: number): Promise<void> {
        const targetEntity: SelectedEntity = _.find(this.additionalSelections, { id: entityTypeId });

        targetEntity.loading = true;

        try {
            await this.getSelectionData(entityTypeId, targetEntity);
            this.configureAdditionalSelections();
        } finally {
            targetEntity.loading = false;
            this.entityRowsChanged.emit();
        }
    }

    async initStateSource() {
        await this.selectionButtonClicked(EntityTypeIds.STATE);
    }

    async propCharChanged(entity: SelectedEntity, i: number): Promise<void> {
        const payload: GetPropCharValuesRequest = new GetPropCharValuesRequest();
        Object.assign(payload, this.selectEntitiesService.getPayloadSourceEntityData(this.entitySelection, i, true));
        payload.selectedPropCharName = entity.selectedDropdown;

        entity.loading = true;

        const type = entity.id === 999 ? 'site' : 'parcel';
        try {
            const propCharValues = await this.selectEntitiesService.getPropCharValues(type, payload);
            entity.list = propCharValues.map((val: any) => {
                let tempValue = val;

                if (tempValue instanceof Date && !isNaN(tempValue.getTime())) {
                    tempValue = this._datePipe.transform(val, true);
                }
                return {
                    id: null,
                    name: tempValue,
                };
            });
            entity.list.sort((x, y) => x.name.toLowerCase().localeCompare(y.name.toLowerCase()));

            this.removeEmptyEntities(i);
        } finally {
            entity.loading = false;
        }
    }

    selectAllTopLevelCompanies(): void {
        this.entitySelection.selectedTopLevelCompanyIDs = _.map(this.topLevelCompanies, 'companyID') as number[];

        this.maybeGetSubentities(-1);
    }

    apply(): void {
        this.entitySelectionChanged.emit(true);
    }

    toggleBody(): void {
        this.showBodyChange.emit(!this.showBody);
    }

    unselectAllTopLevelCompanies(): void {
        this.entitySelection.selectedTopLevelCompanyIDs = [];

        this.maybeGetSubentities(-1);
    }

    selectAll(entity: SelectedEntity, i: number): void {
        if (entity.id == 999 || entity.id == 998) {
            entity.selectedPropCharVals = _.map(entity.list, 'name') as string[];
        } else {
            entity.selectedIDs = _.map(entity.list, 'id');
        }

        this.maybeGetSubentities(i);
    }

    unselectAll(entity: SelectedEntity, i: number): void {
        if (entity.id == 999 || entity.id == 998) {
            entity.selectedPropCharVals = [];
        } else {
            entity.selectedIDs = [];
        }

        this.maybeGetSubentities(i);
    }

    areAllSelected(entity: SelectedEntity): boolean {
        if (entity.id == 999 || entity.id == 998) {
            return entity.selectedPropCharVals.length == entity.list.length;
        } else {
            return entity.selectedIDs.length == entity.list.length;
        }
    }

    removeLast(): void {
        const last: SelectedEntity = _.last(this.entitySelection.selectedEntities);
        if (last && (last.id === 999 || last.id === 998)) {
            this.reportManagerService.setNewPropChars([]);
        }

        this.entitySelection.selectedEntities.pop();

        this.configureAdditionalSelections();
        this.entityRowsChanged.emit();
    }

    selectedTopLevelCompaniesChanged(selectedTopLevelCompanyIds: number[]): void {
        if (selectedTopLevelCompanyIds.length > 1 && this._reportDisallowsMultipleTLCs()) {
            this._toastr.error('Limit to only one Top Level Company for this report');

            // Keep only the first selected top-level company. Callers driving this from the native
            // select are responsible for reconciling the rejected options back to the DOM.
            selectedTopLevelCompanyIds = selectedTopLevelCompanyIds.slice(0, 1);
        }

        this.entitySelection.selectedTopLevelCompanyIDs = selectedTopLevelCompanyIds;

        this.reportManagerService.topLevelCompanies$.next({
            topLevelCompanyIds: this.entitySelection.selectedTopLevelCompanyIDs,
            initFromSavedReport: false,
        });

        this.configureAdditionalSelections();
        this.maybeGetSubentities(-1);
    }

    selectedSubEntitiesChanged(selectedValues: (number | string)[], entity: SelectedEntity, i: number): void {
        if (entity.id === 999 || entity.id == 998) {
            entity.selectedPropCharVals = selectedValues as string[];
        } else {
            // [ngValue] preserves the original id type (numeric entity ids or user GUID strings).
            entity.selectedIDs = selectedValues as number[];
        }

        this.maybeGetSubentities(i);
    }

    showAdditionalSelections(): boolean {
        return (
            this.additionalSelections.length &&
            (!this.entitySelection.selectedEntities.length || _.last(this.entitySelection.selectedEntities).list.length)
        );
    }

    noItemsSelected(): boolean {
        if (this.entitySelection.selectedEntities.length) {
            const lastEntity: SelectedEntity = _.last(this.entitySelection.selectedEntities);

            return lastEntity.selectedIDs.length == 0 && lastEntity.selectedPropCharVals.length == 0;
        } else {
            return this.entitySelection.selectedTopLevelCompanyIDs.length == 0 && !this.hideTopLevelCompanyPicker;
        }
    }

    isAppealResponsibilitySelected() {
        return this.entitySelection && _.some(this.entitySelection.selectedEntities, 'isAppealResponsibility');
    }

    isTopLevelCompanySelected(companyId: number): boolean {
        // A shared empty array is used as the fallback so a missing selection does not allocate a new
        // array (and therefore invalidate the cache) on every option, on every change detection pass.
        const selectedIds: readonly number[] =
            this.entitySelection?.selectedTopLevelCompanyIDs ??
            ReportManagerSelectEntitiesComponent._noTopLevelCompaniesSelected;

        // Rebuild only when the array is replaced or changes size, which covers every mutation this
        // component performs (selection change, select all, unselect all, and the load intersection).
        if (
            selectedIds !== this._selectedTopLevelCompanySource ||
            selectedIds.length !== this._selectedTopLevelCompanyCount
        ) {
            this._selectedTopLevelCompanySource = selectedIds;
            this._selectedTopLevelCompanyCount = selectedIds.length;
            this._selectedTopLevelCompanyIds = new Set(selectedIds);
        }

        return this._selectedTopLevelCompanyIds.has(companyId);
    }

    selectedTopLevelCompanyOptionsChanged(e: Event): void {
        const select = e.target as HTMLSelectElement;
        const selectedIds: number[] = Array.from(select.selectedOptions, (option) => Number(option.value));

        this.selectedTopLevelCompaniesChanged(selectedIds);

        // The handler can reject part of the selection (report types limited to a single top-level
        // company). Angular only writes [selected] when the bound expression changes, and a rejected
        // option's bound value was already false, so it would stay highlighted in the DOM. Reconcile
        // the option state against the model, but only when the handler actually changed something so
        // the O(n) loop stays off the normal selection path.
        if (!_.isEqual(selectedIds, this.entitySelection.selectedTopLevelCompanyIDs)) {
            for (const option of Array.from(select.options)) {
                option.selected = this.isTopLevelCompanySelected(Number(option.value));
            }
        }
    }

    selectedInstanceChanged(e): void {
        this.entitySelection.selectedSupportInstanceIds = _.map(e.target.selectedOptions, (option) =>
            Number(option.value),
        ) as number[];
    }

    async loadInstances(): Promise<void> {
        this.loadingSupportInstances = true;

        try {
            this.reportFavoriteChange.emit(this.report.criteria.frequentlyUsed);

            const instances = await this.selectEntitiesService.getSupportInstances();
            this.supportInstances = _.sortBy(instances, (item) => item.instanceName.toLowerCase());
        } finally {
            this.loadingSupportInstances = false;
        }

        const instanceIds: number[] = _.map(this.supportInstances, 'instanceId') as number[];
        this.entitySelection.selectedSupportInstanceIds = _.intersection(
            this.entitySelection.selectedSupportInstanceIds,
            instanceIds,
        ) as number[];
    }

    selectAllSupportInstances(): void {
        this.entitySelection.selectedSupportInstanceIds = _.map(this.supportInstances, 'instanceId') as number[];
    }

    unselectAllSupportInstances(): void {
        this.entitySelection.selectedSupportInstanceIds = [];
    }

    //
    // Private Helper Functions
    //

    private async maybeGetSubentities(i: number): Promise<any> {
        if (i === this.entitySelection.selectedEntities.length - 1) {
            return Promise.resolve();
        }

        return this.getNextEntities(i + 1);
    }

    private async getSelectionData(entityTypeId: number, targetEntity: SelectedEntity): Promise<void> {
        const i = this.entitySelection.selectedEntities.length - 1;
        const payload: GetEntitiesRequest = this.selectEntitiesService.getPayloadSourceEntityData(
            this.entitySelection,
            i,
        );

        this.entitySelection.selectedEntities.push(targetEntity);

        if (entityTypeId === 999 || entityTypeId === 998) {
            const type = entityTypeId === 999 ? 'site' : 'parcel';
            const parentPropChars = await this.selectEntitiesService.getPropCharsFromParent(type, payload);
            targetEntity.dropdownList = _.chain(parentPropChars)
                .map('name')
                .sortBy(parentPropChars, (item) => item.toLowerCase())
                .value() as string[];

            this.reportManagerService.setNewPropChars(parentPropChars);
        } else {
            payload.targetEntityTypeID = entityTypeId;
            const parentEntities = await this.getEntitiesFromParent(payload);

            targetEntity.list = _.sortBy(parentEntities, (item) => item.name.toLowerCase()) as GenericEntity[];
        }
    }

    private async getEntitiesFromParent(payload: GetEntitiesRequest): Promise<GenericEntity[]> {
        if (
            payload.targetEntityTypeID === EntityTypeIds.USER &&
            this.report.reportTypeId === this.reportTypes.UserAccess
        ) {
            return await this.selectEntitiesService.getUsersWithAccessToCertainCompanies(
                payload,
                this.report.criteria.includeInternalUsers,
            );
        } else if (payload.targetEntityTypeID === EntityTypeIds.CLIENTSERVICE) {
            return await this.selectEntitiesService.getClientServices(payload);
        } else if (payload.targetEntityTypeID === EntityTypeIds.PROPERTYTYPE) {
            return await this.selectEntitiesService.getPropertyTypes(payload);
        } else if (payload.targetEntityTypeID === 997) {
            return await this.selectEntitiesService.getSiteManagers(payload);
        } else if (payload.targetEntityTypeID === EntityTypeIds.USER) {
            return await this.selectEntitiesService.getUserEntities(payload);
        } else if (
            payload.targetEntityTypeID === EntityTypeIds.STATE &&
            this.report.reportTypeId === this.reportTypes.AttachmentBundlerJurisdiction
        ) {
            return await this.selectEntitiesService.getAllStates(payload);
        } else {
            return await this.selectEntitiesService.getEntitiesFromParent(payload);
        }
    }

    private removeEmptyEntities(i: number): void {
        this.entitySelection.selectedEntities = _.take(
            this.entitySelection.selectedEntities,
            i + 1,
        ) as SelectedEntity[];

        this.configureAdditionalSelections();
    }

    private configureAdditionalSelections(): void {
        const allSelectedEntityOptions: SelectedEntity[] = this.selectEntitiesService.getSelectedEntities();

        this.additionalSelections = _.filter(
            allSelectedEntityOptions,
            (selectedEntityOption: SelectedEntity): boolean => {
                //the entity type ids of the SelectedEntities that the user has chosen from the ui.
                const selectedEntityTypeIds: number[] = _.map(this.entitySelection.selectedEntities, 'id');

                //the selected entity option is only available to the user if they've chosen at least one entity of each of the prerequisite entity types
                const preReqsSatisfied: boolean =
                    _.intersection(selectedEntityTypeIds, selectedEntityOption.prerequisites).length ===
                    selectedEntityOption.prerequisites.length;

                //the selected entity option is only available to the user if they've chosen zero entities of the blocked entity types
                const notBlocked: boolean =
                    _.intersection(selectedEntityTypeIds, selectedEntityOption.blockers).length === 0;

                const maybeAppealResp =
                    !selectedEntityOption.isAppealResponsibility ||
                    (this.entitySelection.selectedTopLevelCompanyIDs.length < 2 &&
                        this.report.reportTypeId === this.reportTypes.Site);

                const maybeContacts =
                    selectedEntityOption.id !== EntityTypeIds.CONTACT ||
                    (this.entitySelection.selectedTopLevelCompanyIDs.length < 2 &&
                        this.report.reportTypeId === this.reportTypes.UserActivity);

                const maybeUsers =
                    selectedEntityOption.id !== EntityTypeIds.USER ||
                    selectedEntityOption.isAppealResponsibility ||
                    this.report.reportTypeId === this.reportTypes.UserAccess;

                const maybeSupportUsers =
                    selectedEntityOption.id !== EntityTypeIds.SUPPORTUSER ||
                    this.report.reportTypeId === this.reportTypes.UserAccess ||
                    this.report.reportTypeId === this.reportTypes.UserActivity ||
                    this.report.reportTypeId === this.reportTypes.SupportInstanceEscalationActivity;

                const maybeSubsidiaries =
                    selectedEntityOption.id != EntityTypeIds.COMPANY ||
                    _.intersection(selectedEntityTypeIds, [EntityTypeIds.USER]).length === 0 ||
                    this.report.reportTypeId === this.reportTypes.Site ||
                    this.report.reportTypeId !== this.reportTypes.AttachmentBundlerJurisdiction;

                const maybeSites =
                    selectedEntityOption.id != EntityTypeIds.SITE ||
                    this.report.reportTypeId !== this.reportTypes.AttachmentBundlerJurisdiction;

                const maybeParcels =
                    selectedEntityOption.id != EntityTypeIds.PARCEL ||
                    this.report.reportTypeId !== this.reportTypes.AttachmentBundlerJurisdiction;

                const maybeSiteCharacheristics =
                    selectedEntityOption.id != 999 ||
                    this.report.reportTypeId !== this.reportTypes.AttachmentBundlerJurisdiction;

                const maybeUserActivityReport =
                    this.report.reportTypeId != this.reportTypes.UserActivity ||
                    selectedEntityOption.id === EntityTypeIds.CONTACT ||
                    selectedEntityOption.id === EntityTypeIds.COMPANY;

                const maybeUserAccessReport =
                    this.report.reportTypeId != this.reportTypes.UserAccess ||
                    selectedEntityOption.id === EntityTypeIds.USER ||
                    selectedEntityOption.id === EntityTypeIds.COMPANY;

                const maybeSiteManagers =
                    selectedEntityOption.id !== 997 ||
                    this.report.reportTypeId === this.reportTypes.ARBValue ||
                    this.report.reportTypeId === this.reportTypes.ARBCoverLetter ||
                    this.report.reportTypeId === this.reportTypes.TaxSavings;

                const maybeCollectors =
                    selectedEntityOption.id !== EntityTypeIds.COLLECTOR ||
                    this.report.reportTypeId === this.reportTypes.AttachmentBundlerJurisdiction;

                //condition is true if CsrCommandCenterReport OR not one of these 3 entity type (filters)
                const maybeCsrCommandCenterReport =
                    this.report.reportTypeId === this.reportTypes.CsrCommandCenter ||
                    !(
                        selectedEntityOption.id === EntityTypeIds.CLIENTSERVICE ||
                        selectedEntityOption.id === EntityTypeIds.PROPERTYTYPE ||
                        selectedEntityOption.id === 998
                    );

                return (
                    preReqsSatisfied &&
                    notBlocked &&
                    maybeAppealResp &&
                    maybeContacts &&
                    maybeUserActivityReport &&
                    maybeUserAccessReport &&
                    maybeUsers &&
                    maybeSupportUsers &&
                    maybeSubsidiaries &&
                    maybeSites &&
                    maybeParcels &&
                    maybeSiteCharacheristics &&
                    maybeSiteManagers &&
                    maybeCollectors &&
                    maybeCsrCommandCenterReport
                );
            },
        ) as SelectedEntity[];
    }

    private async getNextEntities(i: number): Promise<any> {
        const entity: SelectedEntity = this.entitySelection.selectedEntities[i];
        const payload: any = this.selectEntitiesService.getPayloadSourceEntityData(this.entitySelection, i - 1);

        entity.loading = true;

        try {
            if (entity.id === 999 || entity.id === 998) {
                const type = entity.id === 999 ? 'site' : 'parcel';
                const parentPropChars = await this.selectEntitiesService.getPropCharsFromParent(type, payload);
                return this.processPropChars(entity, i, parentPropChars);
            } else {
                payload.targetEntityTypeID = entity.id;

                const parentEntities = await this.getEntitiesFromParent(payload);
                return this.processEntity(entity, i, parentEntities);
            }
        } finally {
            entity.loading = false;
        }
    }

    private async processPropChars(entity: SelectedEntity, i: number, propChars: GenericEntity[]): Promise<any> {
        entity.dropdownList = _.map(propChars, 'name');

        if (!_.includes(entity.dropdownList, entity.selectedDropdown)) {
            entity.list = [];
            entity.selectedDropdown = '';
            this.removeEmptyEntities(i);

            return Promise.resolve();
        }

        await this.propCharChanged(entity, i);

        i++;
        if (i < this.entitySelection.selectedEntities.length) {
            return this.getNextEntities(i);
        } else {
            return Promise.resolve();
        }
    }

    private processEntity(entity: SelectedEntity, i: number, entityList: GenericEntity[]): Promise<any> {
        entity.selectedIDs = _.intersection(entity.selectedIDs, _.map(entityList, 'id'));
        entity.list = _.sortBy(entityList, (item) => item.name.toLowerCase()) as GenericEntity[];

        if (entity.list.length === 0 || entity.selectedIDs.length === 0) {
            this.removeEmptyEntities(i);

            return Promise.resolve();
        }

        i++;

        if (i < this.entitySelection.selectedEntities.length) {
            return this.getNextEntities(i);
        } else {
            return Promise.resolve();
        }
    }

    private _reportDisallowsMultipleTLCs() {
        return (
            this.report.reportTypeId == ReportType.Budget ||
            this.report.reportTypeId == ReportType.BudgetVariance ||
            this.report.reportTypeId == ReportType.Site ||
            this.report.reportTypeId == ReportType.InitialValue ||
            this.report.reportTypeId == ReportType.FinalValue
        );
    }
}
